import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  ButtonVariants,
  ButtonSizes,
  BadgeVariants,
  Typography,
  TypographyVariants,
  TypographyColors,
  RowActionsMenu,
} from '@carboledger/ui';
import { MoreHorizontal } from '@carboledger/ui/icons';
import { useTranslations } from '@/hooks';
import { ROUTES } from '@/app/routes';
import { DECLARATION_STATUS_TONE, VERIFICATION_STATUS_TONE } from '@/config/statusTones';
import { LoadingOverlay, EmptyState, ConfirmDialog } from '@/components/feedback';
import {
  AddAttachmentModal,
  DetailPageHeader,
  DiscrepancyModal,
  SurplusWriteoffModal,
  SURPLUS_WRITEOFF_MODES,
} from '@/components/common';
import { ATTACHMENT_TARGETS } from '@/lib/api/attachmentsApi';
import { useUpdateSurplusWriteoffMutation } from '@/lib/api/surplusWriteoffApi';
import {
  useGetDeclarationOverviewQuery,
  useDeleteDeclarationMutation,
} from '../api/declarationsApi';
import { buildRowMenuActions, resolveRowMenuItems } from '../constants/declarationsColumns';
import { DeclarationDetailTab } from '../constants/declarations.constants';
import { buildDeclarationDiscrepancySections } from '../constants/declarationDiscrepancy';
import { DeclarationOverviewTabView } from '../components/DeclarationOverviewTabView';
import { DeclarationDetailsTabView } from '../components/DeclarationDetailsTabView';
import { DeclarationAllocationsTabView } from '../components/DeclarationAllocationsTabView';
import { DeclarationLinkedShipmentsTabView } from '../components/DeclarationLinkedShipmentsTabView';
import { DeclarationAttachmentsTabView } from '../components/DeclarationAttachmentsTabView';
import { DeclarationEmissionCalculationsTabView } from '../components/DeclarationEmissionCalculationsTabView';
import styles from './DeclarationDetailPage.module.scss';

// Pins these columns so the meta bar's second row lines up under the first.
const META_WIDTHS = Object.freeze({ id: 123, material: 194, short: 90, period: 342 });

// Inbounds ▸ Incoming Declarations ▸ Declaration details.
export const DeclarationDetailPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, declarationId } = useParams();
  const [activeTab, setActiveTab] = useState(DeclarationDetailTab.overview);
  const [isDiscrepancyOpen, setIsDiscrepancyOpen] = useState(false);
  const [isSurplusWriteoffOpen, setIsSurplusWriteoffOpen] = useState(false);
  const [isAttachmentOpen, setIsAttachmentOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteDeclaration, { isLoading: isDeleting }] = useDeleteDeclarationMutation();
  const [updateSurplusWriteoff, { isLoading: isSavingSurplusWriteoff }] =
    useUpdateSurplusWriteoffMutation();

  const {
    data: declaration,
    isLoading,
    isError,
  } = useGetDeclarationOverviewQuery({ id: declarationId });

  const goToDeclarations = useCallback(
    () => navigate(ROUTES.declarations(module)),
    [navigate, module],
  );

  const tabs = useMemo(
    () => [
      { value: DeclarationDetailTab.overview, label: t('SECTION_OVERVIEW') },
      {
        value: DeclarationDetailTab.emissionCalculations,
        label: t('DECLARATIONS_DETAIL_TAB_EMISSION_CALCULATIONS'),
      },
      {
        value: DeclarationDetailTab.declarationDetails,
        label: t('DECLARATIONS_DETAIL_TAB_DECLARATION_DETAILS'),
      },
      {
        value: DeclarationDetailTab.linkedShipments,
        label: t('DECLARATIONS_DETAIL_TAB_LINKED_SHIPMENTS'),
      },
      {
        value: DeclarationDetailTab.allocationDetails,
        label: t('DECLARATIONS_DETAIL_TAB_ALLOCATION_DETAILS'),
      },
      { value: DeclarationDetailTab.attachments, label: t('SECTION_ATTACHMENTS') },
    ],
    [t],
  );

  const breadcrumbItems = useMemo(
    () => [
      { label: t('DECLARATIONS_LIST_TITLE'), onClick: goToDeclarations },
      { label: declaration?.referenceId ?? '-' },
    ],
    [t, goToDeclarations, declaration?.referenceId],
  );

  const handleViewDeclarationFile = useCallback(() => {
    if (declaration?.declarationFileUrl) window.open(declaration.declarationFileUrl, '_blank');
  }, [declaration?.declarationFileUrl]);

  const handleFulfill = useCallback(() => {
    // TODO: route to the fulfill-from-declaration flow once it is built.
  }, []);

  const handleAddAttachment = useCallback(() => setIsAttachmentOpen(true), []);
  const handleCloseAttachment = useCallback(() => setIsAttachmentOpen(false), []);
  const handleEditDeclaration = useCallback(
    () => navigate(ROUTES.declarationEdit(module, declarationId)),
    [navigate, module, declarationId],
  );
  const handleDeleteDeclaration = useCallback(() => setIsDeleteOpen(true), []);
  const handleCancelDelete = useCallback(() => setIsDeleteOpen(false), []);
  const handleConfirmDelete = useCallback(async () => {
    if (isDeleting) return;
    const result = await deleteDeclaration({ module, id: declarationId });
    setIsDeleteOpen(false);
    // The declaration is gone, so its detail route is too.
    if (!result.error) navigate(ROUTES.declarations(module));
  }, [isDeleting, deleteDeclaration, navigate, module, declarationId]);
  const handleSurplusWriteOff = useCallback(() => setIsSurplusWriteoffOpen(true), []);
  const handleCloseSurplusWriteoff = useCallback(() => setIsSurplusWriteoffOpen(false), []);
  const handleSubmitSurplusWriteoff = useCallback(
    async (values) => {
      try {
        await updateSurplusWriteoff({ module, id: declarationId, ...values }).unwrap();
        setIsSurplusWriteoffOpen(false);
      } catch {
        // notifyApiErrors already surfaced it.
      }
    },
    [updateSurplusWriteoff, module, declarationId],
  );

  const menuItems = useMemo(
    () =>
      declaration
        ? resolveRowMenuItems(
            buildRowMenuActions(t, {
              onAddAttachment: handleAddAttachment,
              onEdit: handleEditDeclaration,
              onDelete: handleDeleteDeclaration,
              onSurplusWriteOff: handleSurplusWriteOff,
            }),
            declaration,
          )
        : [],
    [
      t,
      declaration,
      handleAddAttachment,
      handleEditDeclaration,
      handleDeleteDeclaration,
      handleSurplusWriteOff,
    ],
  );

  const handleOpenDiscrepancies = useCallback(() => setIsDiscrepancyOpen(true), []);
  const handleCloseDiscrepancies = useCallback(() => setIsDiscrepancyOpen(false), []);

  const discrepancySections = useMemo(
    () => buildDeclarationDiscrepancySections(t, declaration),
    [t, declaration],
  );

  const metaFields = useMemo(() => {
    if (!declaration) return [];
    // Two rows, not one that wraps.
    return [
      [
        {
          id: 'declarationNo',
          label: t('FIELD_DECLARATION_NUMBER'),
          value: declaration.declarationNo,
          width: META_WIDTHS.id,
        },
        {
          id: 'material',
          label: t('FIELD_MATERIAL_NAME'),
          value: declaration.materialName,
          width: META_WIDTHS.material,
        },
        {
          id: 'sentBy',
          label: t('FIELD_SENT_BY'),
          value: declaration.supplierName,
          width: META_WIDTHS.short,
        },
        {
          id: 'receivedAt',
          label: t('FIELD_RECEIVED_AT'),
          value: declaration.facilityName,
          width: META_WIDTHS.short,
        },
        {
          id: 'terminal',
          label: t('FIELD_TERMINAL_NAME'),
          value: declaration.terminal,
          width: META_WIDTHS.short,
        },
        {
          id: 'status',
          label: t('FIELD_STATUS'),
          value: declaration.status,
          type: 'status',
          tone: DECLARATION_STATUS_TONE[declaration.statusCode] ?? BadgeVariants.default,
          width: META_WIDTHS.short,
        },
        {
          id: 'verificationStatus',
          label: t('FIELD_DECLARATION_VERIFICATION_STATUS'),
          value: declaration.verificationStatus,
          type: 'status',
          tone:
            VERIFICATION_STATUS_TONE[declaration.verificationStatusCode] ?? BadgeVariants.default,
        },
        {
          id: 'discrepancies',
          label: t('FIELD_DISCREPANCIES'),
          count: declaration.discrepancyCount,
          type: 'discrepancy',
          ariaLabel: t('ACTION_VIEW_DISCREPANCIES'),
          onClick: handleOpenDiscrepancies,
        },
      ],
      [
        {
          id: 'massBalancePeriod',
          label: t('ALLOCATIONS_COL_MASS_BALANCE_PERIOD'),
          value: declaration.massBalancePeriod,
          width: META_WIDTHS.period,
        },
        {
          id: 'dateOfDispatch',
          label: t('FIELD_DATE_OF_DISPATCH'),
          value: declaration.dateOfDispatch,
          width: META_WIDTHS.short,
        },
        {
          id: 'dateOfIssuance',
          label: t('DECLARATIONS_COL_DATE_OF_ISSUANCE'),
          value: declaration.dateOfIssuance,
          width: META_WIDTHS.short,
        },
        {
          id: 'dateOfDischarge',
          label: t('FIELD_DATE_OF_DISCHARGE'),
          value: declaration.dateOfDischarge,
          width: META_WIDTHS.short,
        },
        {
          id: 'declarationFile',
          label: t('FIELD_DECLARATION'),
          value: declaration.declarationFileUrl ? (
            <Button
              variant={ButtonVariants.tertiary}
              size={ButtonSizes.sm}
              onClick={handleViewDeclarationFile}
            >
              {t('COMMON_VIEW')}
            </Button>
          ) : (
            <Typography variant={TypographyVariants.bodySm} color={TypographyColors.primary}>
              {'-'}
            </Typography>
          ),
          width: META_WIDTHS.short,
        },
      ],
    ];
  }, [t, declaration, handleOpenDiscrepancies, handleViewDeclarationFile]);

  if (isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !declaration) {
    return (
      <EmptyState
        title={t('DECLARATIONS_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToDeclarations}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <div className={styles.page}>
      <DetailPageHeader
        compactMeta
        breadcrumbItems={breadcrumbItems}
        title={declaration.referenceId}
        actions={
          <>
            {/* TODO: enable once the declaration ⇄ shipment link exists server-side. */}
            <Button variant={ButtonVariants.secondary} size={ButtonSizes.sm} disabled>
              {t('ACTION_LINK_SHIPMENTS')}
            </Button>
            {declaration.canFulfillOrder && (
              <Button
                variant={ButtonVariants.primary}
                size={ButtonSizes.sm}
                onClick={handleFulfill}
              >
                {t('ACTION_FULFILL')}
              </Button>
            )}
            <RowActionsMenu
              items={menuItems}
              ariaLabel={t('COMMON_MORE_ACTIONS')}
              icon={MoreHorizontal}
            />
          </>
        }
        metaFields={metaFields}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabsAriaLabel={t('DECLARATIONS_LIST_TITLE')}
      />

      <div className={styles.content}>
        {activeTab === DeclarationDetailTab.overview && (
          <DeclarationOverviewTabView declaration={declaration} />
        )}

        {activeTab === DeclarationDetailTab.declarationDetails && (
          <DeclarationDetailsTabView declarationId={declarationId} />
        )}

        {activeTab === DeclarationDetailTab.emissionCalculations && (
          <DeclarationEmissionCalculationsTabView declarationId={declarationId} />
        )}

        {activeTab === DeclarationDetailTab.linkedShipments && (
          <DeclarationLinkedShipmentsTabView declarationId={declarationId} />
        )}

        {activeTab === DeclarationDetailTab.allocationDetails && (
          <DeclarationAllocationsTabView declarationId={declarationId} />
        )}

        {activeTab === DeclarationDetailTab.attachments && (
          <DeclarationAttachmentsTabView
            module={module}
            declarationId={declarationId}
            referenceId={declaration.referenceId}
          />
        )}
      </div>

      <DiscrepancyModal
        open={isDiscrepancyOpen}
        onClose={handleCloseDiscrepancies}
        referenceId={declaration.referenceId}
        discrepancies={declaration.discrepancies}
        sections={discrepancySections}
      />

      <AddAttachmentModal
        open={isAttachmentOpen}
        onClose={handleCloseAttachment}
        title={t('DECLARATIONS_ADD_ATTACHMENT_MODAL_TITLE', {
          referenceId: declaration.referenceId,
        })}
        target={ATTACHMENT_TARGETS.declaration}
        module={module}
        entityId={declarationId}
      />

      <ConfirmDialog
        open={isDeleteOpen}
        title={t('DECLARATIONS_DELETE_TITLE')}
        bodyTitle={t('DECLARATIONS_DELETE_BODY_TITLE', { referenceId: declaration.referenceId })}
        description={t('DECLARATIONS_DELETE_DESCRIPTION')}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <SurplusWriteoffModal
        open={isSurplusWriteoffOpen}
        onClose={handleCloseSurplusWriteoff}
        mode={
          declaration.hasSurplusOrWriteOff
            ? SURPLUS_WRITEOFF_MODES.edit
            : SURPLUS_WRITEOFF_MODES.add
        }
        row={declaration}
        onSubmit={handleSubmitSurplusWriteoff}
        isSubmitting={isSavingSurplusWriteoff}
      />
    </div>
  );
};
